"use client"

import type React from "react"
import { createContext, useState, useContext, type ReactNode } from "react"

// Define the available language types
type Language = "es" | "en"

// Define translations directly in the code
const translations = {
  es: {
    login: {
      username: "Usuario",
      password: "Contraseña",
      rememberPassword: "Recordar contraseña",
      login: "Iniciar sesión",
      troubleWithLogin: "¿Problemas con el inicio de sesión? Contacta",
      enterYourPassword: "Ingresa tu contraseña",
      errors: {
        usernameEmpty: "El nombre de usuario no puede estar vacío",
        passwordEmpty: "La contraseña no puede estar vacía",
        passwordLength: "La contraseña debe tener al menos 8 caracteres para mayor seguridad.",
        invalidEmail: "Formato de correo electrónico inválido",
        unexpectedError: "Ocurrió un error inesperado. Por favor, inténtalo de nuevo.",
      },
    },
  },
  en: {
    login: {
      username: "Username",
      password: "Password",
      rememberPassword: "Remember password",
      login: "Log In",
      troubleWithLogin: "Trouble with Log In? Contact",
      enterYourPassword: "Enter your password",
      errors: {
        usernameEmpty: "Username cannot be empty",
        passwordEmpty: "Password cannot be empty",
        passwordLength: "Password must be at least 8 characters long for enhanced security.",
        invalidEmail: "Invalid email format",
        unexpectedError: "An unexpected error occurred. Please try again.",
      },
    },
  },
}

// Create the type for the context
interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
}

// Create the context
const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

// Create the context provider
export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Try to get the language saved in localStorage, or use "es" as default
  const savedLanguage = typeof window !== "undefined" ? (localStorage.getItem("language") as Language) : null
  const [language, setLanguage] = useState<Language>(savedLanguage || "es")

  // Function to change the language
  const changeLanguage = (lang: Language) => {
    setLanguage(lang)
    if (typeof window !== "undefined") {
      localStorage.setItem("language", lang)
    }
  }

  // Function to get a translation
  const t = (key: string): string => {
    const keys = key.split(".")
    let result: any = translations[language]

    // Traverse the chain of keys to access the nested value
    for (const k of keys) {
      if (result && result[k] !== undefined) {
        result = result[k]
      } else {
        console.warn(`Translation key not found: ${key}`)
        return key // If translation is not found, return the key
      }
    }

    return typeof result === "string" ? result : key
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, t }}>{children}</LanguageContext.Provider>
  )
}

// Custom hook to use the context
export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}